import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { ArrowLeft, ExternalLink } from 'lucide-react';
import SEO from '../components/SEO';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ProjectDetail = () => {
  const { slug } = useParams();
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
  fetchProject();
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, []);

  const fetchProject = async () => {
    try {
      const response = await axios.get(`${API}/projects/${slug}`);
      setProject(response.data);
    } catch (error) {
      console.error('Error fetching project:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center pt-32">
        <p className="text-lg font-light text-ink/60">Cargando proyecto...</p>
      </div>
    );
  }

  if (!project) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center pt-32" data-testid="project-not-found">
        <p className="text-lg font-light text-ink/60 mb-8">Proyecto no encontrado</p>
        <Link to="/projects" className="text-editorial hover:underline">
          Volver a Proyectos
        </Link>
      </div>
    );
  }

  return (
    <>
      <SEO title={`${project.title} | Portfolio`} description={project.description} image={project.images[0]} />

      <div className="pt-32 pb-24">
        {/* Back Button */}
        <section className="px-6 md:px-12 lg:px-24 mb-12">
          <div className="max-w-screen-2xl mx-auto">
            <Link
              to="/projects"
              data-testid="back-to-projects"
              className="inline-flex items-center gap-2 text-sm font-sans tracking-widest uppercase hover:text-editorial transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              Volver a Proyectos
            </Link>
          </div>
        </section>

        {/* Project Header */}
        <section className="px-6 md:px-12 lg:px-24 mb-16" data-testid="project-header">
          <div className="max-w-screen-2xl mx-auto">
            <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}>
              <div className="flex items-center gap-4 mb-4">
                <p className="text-sm font-sans tracking-widest uppercase text-editorial">{project.category}</p>
                {project.type === 'ficticio' && (
                  <span className="text-xs font-sans tracking-widest uppercase text-editorial border border-editorial px-3 py-1">
                    Proyecto Conceptual
                  </span>
                )}
              </div>
              <h1 className="text-5xl md:text-7xl font-bold tracking-tight mb-8" data-testid="project-title">
                {project.title}
              </h1>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-8 text-sm font-sans">
                <div>
                  <p className="tracking-widest uppercase text-ink/60 mb-2">Año</p>
                  <p className="text-lg" data-testid="project-year">
                    {project.year}
                  </p>
                </div>
                {project.client && (
                  <div>
                    <p className="tracking-widest uppercase text-ink/60 mb-2">Cliente</p>
                    <p className="text-lg" data-testid="project-client">
                      {project.client}
                    </p>
                  </div>
                )}
                <div>
                  <p className="tracking-widest uppercase text-ink/60 mb-2">Tipo</p>
                  <p className="text-lg" data-testid="project-type">
                    {project.type === 'real' ? 'Proyecto Real' : 'Proyecto Conceptual'}
                  </p>
                </div>
              </div>
            </motion.div>
          </div>
        </section>

        {/* Main Image */}
        <section className="px-6 md:px-12 lg:px-24 mb-16">
          <div className="max-w-screen-2xl mx-auto">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.8 }}
              className="editorial-border"
            >
              <img
                src={project.images[0]}
                alt={project.title}
                className="w-full h-[600px] object-cover"
                data-testid="project-main-image"
              />
            </motion.div>
          </div>
        </section>

        {/* Description */}
        <section className="px-6 md:px-12 lg:px-24 mb-24">
          <div className="max-w-3xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3, duration: 0.8 }}
            >
              <h2 className="text-3xl font-bold tracking-tight mb-6">Descripción</h2>
              <p className="text-lg font-light leading-relaxed text-ink/80" data-testid="project-description">
                {project.description}
              </p>
            </motion.div>
          </div>
        </section>

        {/* Image Gallery */}
        {project.images.length > 1 && (
          <section className="px-6 md:px-12 lg:px-24 mb-24" data-testid="project-gallery">
            <div className="max-w-screen-2xl mx-auto">
              <h2 className="text-3xl font-bold tracking-tight mb-8">Galería</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                {project.images.slice(1).map((image, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.1, duration: 0.6 }}
                    className="editorial-border"
                  >
                    <img src={image} alt={`${project.title} - Image ${index + 2}`} className="w-full h-96 object-cover" loading="lazy" />
                  </motion.div>
                ))}
              </div>
            </div>
          </section>
        )}
      </div>
    </>
  );
};

export default ProjectDetail;
